'use client'

import React from 'react'
import { toast } from 'sonner'
import { useTranslations, useLocale } from 'next-intl'
import { Undo2, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'

/**
 * Right-of-withdrawal ("Vertrag widerrufen") button — legally required in AT
 * from October 2026. A visible floating trigger (bottom-left, every page, no
 * login) that opens a form collecting the data the law requires: name, order
 * number, and contact. On submit the request is emailed to the shop and an
 * acknowledgement (Eingangsbestätigung on a durable medium) is emailed to the
 * customer.
 */
export function WithdrawalButton() {
  const t = useTranslations('withdrawalForm')
  const locale = useLocale()

  const [open, setOpen] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const [done, setDone] = React.useState(false)
  const [form, setForm] = React.useState({
    firstName: '',
    lastName: '',
    orderNumber: '',
    email: '',
    message: '',
  })

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    try {
      const res = await fetch('/api/withdrawal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, locale }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        toast.error(data?.error || t('error'))
        return
      }
      setDone(true)
      toast.success(t('successToast'))
    } catch {
      toast.error(t('error'))
    } finally {
      setSubmitting(false)
    }
  }

  // Reset the form a moment after the dialog closes so a reopen is clean.
  const onOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) {
      setTimeout(() => {
        setDone(false)
        setForm({ firstName: '', lastName: '', orderNumber: '', email: '', message: '' })
      }, 200)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('buttonLabel')}
        className="fixed bottom-4 left-2 z-40 flex items-center gap-2 rounded-full border border-white/15 bg-black/80 px-4 py-2.5 font-mono text-xs uppercase tracking-wider text-white shadow-lg transition-colors hover:border-white/40 hover:bg-black"
      >
        <Undo2 className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="hidden sm:inline">{t('buttonLabel')}</span>
      </button>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          {/* On success we swap the title to the confirmation and drop the
              intro — the "Vertrag widerrufen" heading and the 14-day notice are
              hidden once the request is in. A DialogTitle is always rendered so
              the dialog stays accessible. */}
          <DialogHeader>
            <DialogTitle
              className={done ? 'text-green-600 dark:text-green-400' : undefined}
            >
              {done ? t('confirmTitle') : t('title')}
            </DialogTitle>
            {!done && <DialogDescription>{t('intro')}</DialogDescription>}
          </DialogHeader>

          {done ? (
            <div className="py-2 text-sm" role="status">
              <p className="text-neutral-600 dark:text-neutral-300">{t('confirmBody')}</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <Label htmlFor="wd-first">{t('firstName')}</Label>
                  <Input id="wd-first" required value={form.firstName} onChange={set('firstName')} />
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="wd-last">{t('lastName')}</Label>
                  <Input id="wd-last" required value={form.lastName} onChange={set('lastName')} />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="wd-order">{t('orderNumber')}</Label>
                <Input
                  id="wd-order"
                  required
                  placeholder="STM-…"
                  value={form.orderNumber}
                  onChange={set('orderNumber')}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="wd-email">{t('email')}</Label>
                <Input
                  id="wd-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set('email')}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="wd-msg">
                  {t('message')} <span className="text-neutral-400">({t('optional')})</span>
                </Label>
                <Textarea id="wd-msg" rows={3} value={form.message} onChange={set('message')} />
              </div>

              <Button type="submit" disabled={submitting} className="mt-1 w-full">
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    {t('submitting')}
                  </span>
                ) : (
                  t('submit')
                )}
              </Button>
              <p className="text-xs text-neutral-500">{t('legalNote')}</p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
